'use client';

import { forwardRef, ForwardRefRenderFunction, IframeHTMLAttributes, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { MxpIframeErrorHandler, MxpIframeFavoriteAddedHandler, MxpIframeHeightChangeHandler, MxpIframeIncomingMessage, MxpIframeNavigateHandler, MxpIframeOutgoingMessage, MxpIframeReadyHandler, MxpIframeRef, MxpIframeShareHandler, MxpIframeTitleChangeHandler } from './mxp365.types';
import { usePageViewContext } from '@tuic/tracking';
import { MXP_BASE_URL } from '@lib/config-client';
type Props = IframeHTMLAttributes<HTMLIFrameElement> & {
  path: string;
  token: string;
  queryParams?: Record<string, string>;
  onMxpHeightChange?: MxpIframeHeightChangeHandler;
  onMxpFavoriteAdded?: MxpIframeFavoriteAddedHandler;
  onMxpNavigate?: MxpIframeNavigateHandler;
  onMxpError?: MxpIframeErrorHandler;
  onMxpShare?: MxpIframeShareHandler;
  onMxpTitleChange?: MxpIframeTitleChangeHandler;
  onMxpModuleReady?: MxpIframeReadyHandler;
  onLoad?: () => void;
};
const MXP365_KEEP_ALIVE_INTERVAL_IN_MS = 1 * 60 * 1000;
const MxpIframeRenderFunction: ForwardRefRenderFunction<MxpIframeRef, Props> = ({
  path,
  token,
  queryParams,
  onMxpNavigate,
  onMxpFavoriteAdded,
  onMxpShare,
  onMxpTitleChange,
  onMxpError,
  onMxpHeightChange,
  onMxpModuleReady,
  onLoad,
  ...args
}, forwardedRef) => {
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  let iframeRef = useRef<HTMLIFrameElement>(null);
  const navigationQueueRef = useRef<string[]>([]);
  const pageViewData = usePageViewContext();
  let iframeSource = useMemo(() => {
    // Build the MXP365 URL from structured components
    // Remove leading slash from path if present since we'll add /mxp365tiles
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const urlObj = new URL(`${MXP_BASE_URL}/mxp365tiles${cleanPath}`);

    // Add token
    if (token) {
      urlObj.searchParams.set('token', token);
    }

    // Add query params from the original URL
    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => {
        urlObj.searchParams.set(key, value);
      });
    }

    // Add page view data if available (convert snake_case keys to camelCase
    // as expected by the MXP365 frontend, e.g. page_name → pageName)
    if (pageViewData) {
      Object.entries(pageViewData).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        const camelKey = key.replace(/_(.)/g, (_, c: string) => c.toUpperCase());
        if (!urlObj.searchParams.has(camelKey)) {
          urlObj.searchParams.set(camelKey, String(value));
        }
      });
    }
    return urlObj.toString();
  }, [path, token, queryParams, pageViewData]);
  const postMessage = (message: MxpIframeOutgoingMessage) => {
    try {
      iframeRef.current?.contentWindow?.postMessage(message, '*');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  // Process any queued navigation commands
  const processNavigationQueue = useCallback(() => {
    if (navigationQueueRef.current.length === 0 || !isIframeLoaded) {
      return;
    }

    // Process all queued navigation commands
    const lastTarget = navigationQueueRef.current.pop();
    if (!lastTarget) {
      return;
    }

    // Add /mxp365tiles prefix if it's a path (starts with /)
    const navigationTarget = lastTarget.startsWith('/') ? `/mxp365tiles${lastTarget}` : lastTarget;
    postMessage({
      action: 'navigateTo',
      payload: {
        mxpModule: navigationTarget
      }
    });

    // Clear the queue after processing the last command
    navigationQueueRef.current = [];
  }, [isIframeLoaded]);
  useEffect(() => {
    if (!isIframeLoaded) {
      return;
    }
    processNavigationQueue();
  }, [isIframeLoaded, processNavigationQueue]);
  useImperativeHandle<MxpIframeRef, MxpIframeRef>(forwardedRef, () => {
    return {
      iframe: iframeRef?.current,
      postMessage,
      ping: () => postMessage({
        action: 'ping',
        payload: {
          timePing: new Date().getTime()
        }
      }),
      navigateBack: () => postMessage({
        action: 'navigateBack'
      }),
      navigateTo: pathOrModuleId => {
        if (!isIframeLoaded) {
          // Queue the navigation command if iframe is not loaded
          navigationQueueRef.current.push(pathOrModuleId);
          return;
        }

        // Send the navigation command immediately if iframe is loaded
        // Add /mxp365tiles prefix if it's a path (starts with /)
        const navigationTarget = pathOrModuleId.startsWith('/') ? `/mxp365tiles${pathOrModuleId}` : pathOrModuleId;
        postMessage({
          action: 'navigateTo',
          payload: {
            mxpModule: navigationTarget
          }
        });
      },
      getIframeUrl: () => new URL(iframeSource)
    };
  }, [iframeSource, isIframeLoaded]);
  let handlePostMessage = useCallback((event: MessageEvent) => {
    let message = event.data;
    message.action === 'moduleReady' && onMxpModuleReady?.();
    message.action === 'favoriteAdded' && onMxpFavoriteAdded?.();
    message.action === 'share' && onMxpShare?.(message.payload);
    message.action === 'navigate' && onMxpNavigate?.(message.payload);
    message.action === 'navigationChanged' && onMxpTitleChange?.(message.payload);
    message.action === 'windowHeight' && onMxpHeightChange?.(message.payload);
    if (message.action === 'jwtLoginError' && typeof onMxpError !== 'undefined') {
      handlePostMessageError(onMxpError, message);
    }
  }, [onMxpError, onMxpFavoriteAdded, onMxpShare, onMxpTitleChange, onMxpNavigate, onMxpHeightChange, onMxpModuleReady]);
  const handleIframeError = useCallback(() => {
    if (!onMxpError) {
      return;
    }
    onMxpError({
      type: 'notFound',
      payload: {
        url: iframeSource
      }
    });
  }, [iframeSource, onMxpError]);
  const handleIframeLoad = useCallback(() => {
    setIsIframeLoaded(true);
    onLoad?.();
  }, [onLoad]);
  useEffect(() => {
    window.addEventListener('message', handlePostMessage, false);
    return () => window.removeEventListener('message', handlePostMessage, false);
  }, [handlePostMessage]);

  // Reset iframe loaded state when the iframe source changes
  useEffect(() => {
    setIsIframeLoaded(false);
  }, [iframeSource]);
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      postMessage({
        action: 'ping',
        payload: {
          timePing: new Date().getTime()
        }
      });
    }, MXP365_KEEP_ALIVE_INTERVAL_IN_MS);
    return () => window.clearInterval(intervalId);
  }, []);
  return <iframe ref={iframeRef} src={iframeSource} onError={handleIframeError} onLoad={handleIframeLoad} tabIndex={0} {...args} allow="camera" data-sentry-component="MxpIframeRenderFunction" data-sentry-source-file="Mxp365Iframe.tsx" />;
};
const handlePostMessageError = (handler: MxpIframeErrorHandler, message: MxpIframeIncomingMessage) => {
  if (message.action !== 'jwtLoginError') {
    return;
  }
  if (typeof message.payload === 'undefined') {
    handler({
      type: message.action
    });
    return;
  }
  let json = undefined;
  try {
    json = JSON.parse(message.payload) ?? undefined;
  } catch (e) {}
  handler({
    type: message.action,
    payload: json
  });
};
export const Mxp365Iframe = forwardRef<MxpIframeRef, Props>(MxpIframeRenderFunction);